import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, FolderOpen, Bell, BarChart2,
  Users, LogOut, Database, Scale, ChevronDown
} from 'lucide-react';

const navItems = [
  { to: '/dashboard',     icon: LayoutDashboard, label: 'Dashboard'     },
  { to: '/cases',         icon: FolderOpen,      label: 'Cases'         },
  { to: '/notifications', icon: Bell,            label: 'Notifications' },
  { to: '/reports',       icon: BarChart2,       label: 'Reports'       },
  { to: '/users',         icon: Users,           label: 'Users',  roles: ['admin'] },
  { to: '/masters',       icon: Database,        label: 'Masters', roles: ['admin'] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const visibleNav = navItems.filter(
    item => !item.roles || item.roles.includes(user?.role)
  );

  const initials = user?.full_name
    ?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U';

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f1f5f9' }}>

      {/* ── Top Navigation Bar ─────────────────────────────── */}
      <header className="sticky top-0 z-40 shadow-md" style={{ backgroundColor: '#1a2744' }}>
        <div className="max-w-screen-2xl mx-auto px-6 h-14 flex items-center justify-between gap-4">

          {/* Logo */}
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="bg-white/15 p-1.5 rounded-lg">
              <Scale size={18} className="text-white" />
            </div>
            <span className="text-white font-bold text-lg tracking-tight">Legal Desk</span>
          </div>

          {/* Nav Items */}
          <nav className="flex items-center gap-1 flex-1 justify-center">
            {visibleNav.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${
                    isActive
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-blue-200 hover:text-white hover:bg-white/10'
                  }`
                }
              >
                <Icon size={15} className="shrink-0" />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Right: User Menu */}
          <div className="relative shrink-0">
            <button
              onClick={() => setUserMenuOpen(o => !o)}
              className="flex items-center gap-2 hover:bg-white/10 px-2 py-1.5 rounded-xl transition-colors"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                style={{ backgroundColor: '#6366f1' }}
              >
                {initials}
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-white text-xs font-semibold leading-tight">{user?.full_name}</p>
                <p className="text-blue-300 text-xs capitalize leading-tight">{user?.role?.replace('_', ' ')}</p>
              </div>
              <ChevronDown size={14} className={`text-blue-300 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {userMenuOpen && (
              <>
                {/* Backdrop */}
                <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50">
                  <div className="px-4 py-2.5 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-800 truncate">{user?.full_name}</p>
                    <p className="text-xs text-gray-400 capitalize">{user?.role?.replace('_', ' ')}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={14} />
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── Page Content ───────────────────────────────────── */}
      <main className="flex-1 max-w-screen-2xl mx-auto w-full px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}
